import { Stack, Grid2 as Grid, Button, Link as MuiLink } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { useCustomer } from "../../hooks/useCustomer";
import { PageContext } from "../../context/PageContext";
import LoadingPage from "../../components/LoadingPage";
import { useReadingEmail } from "../../hooks/useReadingEmail";
import { useContacts } from "../../hooks/useContacts";
import { useRedirectToActivities } from "../../hooks/useRedirectToActivities";
import { ContactAlert } from "../../components/ContactAlert";
import { Footer } from "../../components/Footer";
import { InvoiceDetailsModel, InvoiceInfoModel, InvoiceStatus } from "../../models/ChasableModels";
import { InvoiceSelect } from "../Chasables/InvoiceSelect";
import { ContactNotFound } from "./ContactNotFound";
import { ContactInfo } from "./ContactInfo";
import { Receipts } from "./Receipts";
import { useTasksEmail } from "../../hooks/useTasksEmail";
import { TaskModel } from "../../models/TaskModels";
import ActionWithAi from "../../components/ActionWithAi";
import AssignButton from "../../components/AssignButton";
import { InvoiceTotalInfo } from "../../components/InvoiceTotalInfo";
import { useInvoicesByCustomerOrId } from "../../hooks/useInvoicesByCustomerOrId";
import { ChaseButton } from "../../components/ChaseButton";
import { useHandleChase } from "../../hooks/useHandleChase";
import { DraftRecipientMode } from "../../utils/contactRecipients";
import { EmailTemplateResponse } from "../../models/EmailTemplateModels";
import { CompleteButton } from "../../components/CompleteButton";
import { useCompleteChasables } from "../../hooks/useCompleteChasables";

const Receivables = () => {
  const { tasks } = useTasksEmail();
  const [searchParams] = useSearchParams();
  const hideBackButton = !!searchParams.get("hideBackButton");
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [hasBeenChased, setHasBeenChased] = useState(false);
  const { customerId } = useParams<{ customerId: string }>();
  const { emailAddress } = useReadingEmail();
  const { contacts, ...contactsResponse } = useContacts(emailAddress);
  const contact = contacts?.find(({ id }) => id === customerId);
  const { customer, ...customerResponse } = useCustomer(customerId);
  const { invoices } = useInvoicesByCustomerOrId({ id: customerId ?? "", type: "Contact" });
  const validInvoices = invoices.filter(({ status }) => {
    return status !== InvoiceStatus.DELETED && status !== InvoiceStatus.VOIDED;
  });
  const { setNewTitle } = useContext(PageContext);

  const {
    handleChase,
    handleDraftFromTemplate,
    isLoading: isChasing,
  } = useHandleChase({
    invoiceIds: selectedInvoiceIds,
    customerId: customerId,
    contacts: customer?.contacts,
  });
  const { completeChasables, isLoading: isCompleting } = useCompleteChasables();

  useEffect(() => {
    setNewTitle("Receivables");
  }, [setNewTitle]);

  useRedirectToActivities(emailAddress);

  const extraActions: TaskModel[] = useMemo(
    () => [
      ...(!hideBackButton
        ? ([
            {
              name: "Back",
              link: "/contacts",
              linkType: "Internal",
            },
          ] as TaskModel[])
        : []),
      ...(tasks ?? []),
    ],
    [hideBackButton, tasks]
  );

  if (
    contactsResponse.isLoading ||
    customerResponse.isLoading ||
    contactsResponse.isError ||
    customerResponse.isError
  ) {
    return <LoadingPage />;
  }

  if (!customer) {
    return <ContactNotFound email={emailAddress ?? ""} />;
  }

  const handleChaseClick = async (languageId?: string, recipientMode?: DraftRecipientMode) => {
    await handleChase(undefined, undefined, languageId, recipientMode);
    setHasBeenChased(true);
  };
  const handleEmailTemplateClick = async (template: EmailTemplateResponse, recipientMode?: DraftRecipientMode) => {
    await handleDraftFromTemplate(template, recipientMode);
    setHasBeenChased(true);
  };
  const handleCompleteClick = async () => {
    await completeChasables(selectedInvoiceIds);
    setSelectedInvoiceIds([]);
    setHasBeenChased(false);
  };

  return (
    <>
      <Stack spacing={2} pb={selectedInvoiceIds.length > 0 ? 14 : 8}>
        <ContactInfo
          title="Customer"
          contactLink={customer.link}
          companyName={customer.companyName}
          accountNumber={customer.accountNumber}
          defaultUserId={customer.defaultUserId}
        />
        {emailAddress && (
          <ContactAlert
            isSaved={contact?.isSaved ?? false}
            email={emailAddress}
            companyName={customer.companyName}
            contactLink={customer.link}
            integration={customer.integration}
            accountNumber={customer.accountNumber}
          />
        )}
        {customer.totalAmount && (
          <InvoiceTotalInfo
            totalAmount={customer.totalAmount}
            totalDueAmount={customer.totalDueAmount}
            link={customer.link}
          />
        )}
        <Invoices
          invoices={validInvoices}
          customerLink={customer.link}
          selectedInvoiceIds={selectedInvoiceIds}
          setSelectedInvoiceIds={setSelectedInvoiceIds}
        />
        <Receipts receipts={customer.receipts} customerLink={customer.link} />
      </Stack>
      <Footer>
        <Grid container justifyContent="space-between" width="100%">
          {selectedInvoiceIds.length > 0 && (
            <>
              {hasBeenChased ? (
                <Grid size={12}>
                  <CompleteButton onClick={handleCompleteClick} loading={isCompleting} disabled={isCompleting} />
                </Grid>
              ) : (
                <Grid size={12}>
                  <ChaseButton
                    onClick={handleChaseClick}
                    loading={isChasing}
                    disabled={isChasing}
                    contacts={customer.contacts}
                    invoiceCount={selectedInvoiceIds.length}
                    onEmailTemplateClick={handleEmailTemplateClick}
                  />
                </Grid>
              )}
            </>
          )}
          {selectedInvoiceIds.length === 0 && (
            <>
              <Grid size={6}>
                <ActionWithAi actions={extraActions} relatedIds={[]} />
              </Grid>
              <Grid size={6}>
                <AssignButton contactId={contact?.id} />
              </Grid>
            </>
          )}
        </Grid>
      </Footer>
    </>
  );
};

function Invoices({
  invoices,
  customerLink,
  selectedInvoiceIds,
  setSelectedInvoiceIds,
}: {
  invoices: InvoiceDetailsModel[];
  customerLink?: string;
  selectedInvoiceIds: string[];
  setSelectedInvoiceIds: (ids: string[]) => void;
}) {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();

  const handleInvoiceClick = (_: React.MouseEvent<HTMLTableRowElement, MouseEvent>, id: string) => {
    navigate(`/receivables/${customerId}/${id}`);
  };

  const renderTableFooter = () => {
    return (
      <Grid container justifyContent="center" pt={0.5}>
        <Button variant="text" size="small" href={customerLink ?? ""} target="_blank" endIcon={<OpenInNewIcon />}>
          More details
        </Button>
      </Grid>
    );
  };

  const renderLink = (invoice: InvoiceInfoModel) => {
    const invoiceLink = `/receivables/${customerId}/${invoice.id}`;
    return (
      <MuiLink component={Link} to={invoiceLink} underline="hover">
        {invoice.invoiceNumber}
      </MuiLink>
    );
  };

  return (
    <InvoiceSelect
      invoices={invoices}
      selectable={true}
      selectedInvoiceIds={selectedInvoiceIds}
      onSelectChange={setSelectedInvoiceIds}
      onInvoiceClick={handleInvoiceClick}
      tableFooter={renderTableFooter()}
      renderLink={renderLink}
    />
  );
}

export default () => {
  return <Receivables />;
};
